<script setup>
import FullCalendar from "@fullcalendar/vue3"
import dayGridPlugin from "@fullcalendar/daygrid"
import timeGridPlugin from "@fullcalendar/timegrid"
import interactionPlugin from "@fullcalendar/interaction"
import DialogModal from "@/Components/DialogModal.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import {ref, watch} from "vue";
import InputLabel from "@/Components/InputLabel.vue";
import TextInput from "@/Components/TextInput.vue";
import SelectInput from "@/Components/SelectInput.vue";
import '@vuepic/vue-datepicker/dist/main.css'
import Datepicker from "@vuepic/vue-datepicker";
import {usePage} from "@inertiajs/inertia-vue3";
import {useHelpers} from "@/helpers";
import SecondaryButton from "@/Components/SecondaryButton.vue";

defineProps({
  users: Array,
});

const helpers = useHelpers();
const currentUser = usePage().props.value.auth.user;
const calendarRef = ref(null);
const eventType = ref(['pto', 'business', 'other']);
const defaultEventFormData = {
  id: null,
  title: null,
  description: null,
  startDate: null,
  endDate: null,
  status: 'approved',
  type: 'other',
  userId: parseInt(currentUser.employeeID)
};
const eventForm = ref(defaultEventFormData);
const creatingNewEvent = ref({open: false, saving: false, error: null});

watch(eventType, () => {
  calendarRef.value.getApi().refetchEvents()
});

const startCreatingNewEvent = (event) => {
  resetEventForm();
  creatingNewEvent.value.open = true;
  eventForm.value.startDate = dayjs(event.date).format('YYYY-MM-DD');
  eventForm.value.endDate = dayjs(event.date).format('YYYY-MM-DD');
  eventForm.value.type = 'other';
}

const showExistingEvent = (calendarEvent) => {
  const props = calendarEvent.event._def.extendedProps;
  eventForm.value.id = props.id;
  eventForm.value.title = props.title;
  eventForm.value.description = props.description;
  eventForm.value.userId = props.user_id;
  eventForm.value.startDate = props.start;
  eventForm.value.endDate = props.end;
  eventForm.value.status = props.status;
  eventForm.value.type = props.type;
  creatingNewEvent.value.open = true;
  creatingNewEvent.value.saving = false;
}

const updateExistingEvent = (dropEvent) => {
  const props = dropEvent.event._def.extendedProps;
  eventForm.value.id = props.id;
  eventForm.value.title = props.title;
  eventForm.value.description = props.description;
  eventForm.value.userId = props.user_id;
  eventForm.value.type = props.type;
  eventForm.value.startDate = dayjs(dropEvent.event._instance.range.start).format('YYYY-MM-DD');
  eventForm.value.endDate = dayjs(dropEvent.event._instance.range.end).format('YYYY-MM-DD');

  saveEventForm();
}

const closeCreatingNewEvent = () => {
  creatingNewEvent.value.open = false;
  resetEventForm();
}

const saveEventForm = () => {
  creatingNewEvent.value.saving = true;
  let formData = {
    _method: 'POST',
    title: eventForm.value.title,
    description: eventForm.value.description,
    start_date: eventForm.value.startDate,
    end_date: eventForm.value.endDate,
    user_id: eventForm.value.userId,
    type: eventForm.value.type,
  }
  let url = route('internal.office-calendar.store');
  if (eventForm.value.id) {
    url = route('internal.office-calendar.update', {calendar_event: eventForm.value.id});
    formData._method = 'PUT';
  }

  axios.post(url, formData)
    .then(response => {
      creatingNewEvent.value.saving = false;
      closeCreatingNewEvent();
      calendarRef.value.getApi().refetchEvents();
    })
    .catch(error => {
      creatingNewEvent.value.saving = false;
      creatingNewEvent.value.error = error.response.data.message;
    });
}

const deleteEvent = () => {
  if (!eventForm.value.id) {
    return;
  }
  const url = route('internal.office-calendar.destroy', {calendar_event: eventForm.value.id});
  axios.delete(url)
    .then(response => {
      creatingNewEvent.value.saving = false;
      closeCreatingNewEvent();
      calendarRef.value.getApi().refetchEvents();
    })
    .catch(error => {
      creatingNewEvent.value.saving = false;
      creatingNewEvent.value.error = error.response.data.message;
    });
}

const resetEventForm = () => {
  creatingNewEvent.value.error = null;
  eventForm.value.id = null;
  eventForm.value.title = null;
  eventForm.value.description = null;
  eventForm.value.startDate = null;
  eventForm.value.endDate = null;
  eventForm.value.status = 'approved';
  eventForm.value.type = 'other';
  eventForm.value.userId = parseInt(currentUser.employeeID);
}

const isModifyEventDisabled = () => {
  if (eventForm.value.type === 'purchase_order') {
    return true;
  }
  if (!eventForm.value.id) {
    return false;
  }
  if (helpers.userIsAdmin(currentUser)) {
    return false;
  }

  return parseInt(currentUser.employeeID) !== eventForm.value.userId;
}

const settings = {
  options: {
    editable: true,
    initialView: 'dayGridMonth',
    weekends: true,
    events: {
      url: route('internal.office-calendar.events'),
      extraParams: () => {
        let index = 0;
        let payload = {};
        eventType.value.forEach(value => {
          payload[`eventTypes[${index}]`] = value;
          index++;
        });

        return payload;
      }
    },
    dateClick: startCreatingNewEvent,
    eventClick: showExistingEvent,
    eventDrop: updateExistingEvent,
    eventDidMount: (calendarEvent) => {
      // Create the icon
      let icon = document.createElement("i");
      if (calendarEvent.event._def.extendedProps.type === 'pto') {
        icon.classList.add('fa', 'fa-money-bill-alt', 'mr-1');
      } else if (calendarEvent.event._def.extendedProps.type === 'business') {
        icon.classList.add('fa', 'fa-suitcase', 'mr-1');
      } else if (calendarEvent.event._def.extendedProps.type === 'other') {
        icon.classList.add('fa', 'fa-cog', 'mr-1');
      }
      // Add icon before the title
      calendarEvent.el.querySelector(".fc-event-title").prepend(icon);
    },
    plugins: [
      dayGridPlugin, timeGridPlugin, interactionPlugin
    ],
  }
};
</script>

<template>
  <div class="mb-4">
    <ul class="items-center text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-lg inline-flex">
      <li class="border-b border-gray-200 sm:border-b-0 sm:border-r">
        <div class="flex items-center px-3">
          <input id="vue-checkbox-list" type="checkbox" value="pto"
                 class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                 multiple v-model="eventType">
          <label for="vue-checkbox-list" class="w-full py-3 ms-2 text-sm font-medium text-gray-900">PTO</label>
        </div>
      </li>
      <li class="border-b border-gray-200 sm:border-b-0 sm:border-r">
        <div class="flex items-center px-3">
          <input id="react-checkbox-list" type="checkbox" value="business"
                 class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                 multiple v-model="eventType">
          <label for="react-checkbox-list" class="w-full py-3 ms-2 text-sm font-medium text-gray-900">Business</label>
        </div>
      </li>
      <li class="border-b border-gray-200 sm:border-b-0 sm:border-r">
        <div class="flex items-center px-3">
          <input id="angular-checkbox-list" type="checkbox" value="other"
                 class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                 multiple v-model="eventType">
          <label for="angular-checkbox-list" class="w-full py-3 ms-2 text-sm font-medium text-gray-900">Other</label>
        </div>
      </li>
    </ul>
  </div>
  <FullCalendar
    ref="calendarRef"
    :options="settings.options"
    timeZone="UTC"
  />
  <DialogModal :show="creatingNewEvent.open" @close="closeCreatingNewEvent" v-if="creatingNewEvent.open">
    <template #title>
      Calendar Event
    </template>

    <template #content>
      <p v-if="creatingNewEvent.error" class="text-red-800 bg-red-300 p-2 mb-4 rounded"
         v-text="creatingNewEvent.error"></p>
      <div class="sm:grid grid-cols-6 gap-6">
        <!-- Start Date -->
        <div class="col-span-6 sm:col-span-12">
          <InputLabel for="start-date" value="Start Date" :required="true"/>
          <Datepicker class="mt-1 block w-full disabled:bg-gray-50" v-model="eventForm.startDate" format="MMMM d, yyyy"
                      id="start-date" model-type="yyyy-MM-dd" auto-apply :close-on-auto-apply="true"
                      :disabled="isModifyEventDisabled()" :enable-time-picker="false"/>
        </div>
        <!-- End Date -->
        <div class="col-span-6 sm:col-span-12">
          <InputLabel for="end-date" value="End Date" :required="true"/>
          <Datepicker class="mt-1 block w-full disabled:bg-gray-50" v-model="eventForm.endDate" format="MMMM d, yyyy"
                      id="end-date" model-type="yyyy-MM-dd" auto-apply :close-on-auto-apply="true"
                      :disabled="isModifyEventDisabled()" :enable-time-picker="false"/>
        </div>
        <!-- Title -->
        <div class="col-span-6 sm:col-span-12">
          <InputLabel for="title" value="Title" :required="true"/>
          <TextInput
            id="title"
            type="text"
            class="mt-1 block w-full"
            v-model="eventForm.title"
            :disabled="isModifyEventDisabled()"
            autofocus
          />
        </div>
        <!-- Title -->
        <div class="col-span-6 sm:col-span-12" v-if="eventForm.id">
          <InputLabel for="status" value="Status"/>
          <TextInput
            id="status"
            type="text"
            class="mt-1 block w-full text-capitalize"
            :value="eventForm.status.replace('_', '')"
            :disabled="true"
            autofocus
          />
        </div>
        <!-- Description -->
        <div class="col-span-6 sm:col-span-12">
          <InputLabel for="description" value="Description"/>
          <textarea
            rows="5"
            id="description"
            v-model="eventForm.description"
            :disabled="isModifyEventDisabled()"
            class="border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm mt-1 block w-full disabled:bg-gray-50"
          ></textarea>
        </div>
        <div class="col-span-6 sm:col-span-12" v-if="eventForm.type !== 'purchase_order'">
          <InputLabel value="Event type"/>
          <label for="event-type-pto" class="mr-5">
            <input type="radio" v-model="eventForm.type" value="pto" id="event-type-pto"/>
            <span class="ml-1">PTO</span>
          </label>
          <label for="event-type-business" class="mr-5">
            <input type="radio" v-model="eventForm.type" value="business" id="event-type-business"/>
            <span class="ml-1">Business</span>
          </label>
          <label for="event-type-other" class="mr-5">
            <input type="radio" v-model="eventForm.type" value="other" id="event-type-other"/>
            <span class="ml-1">Other</span>
          </label>
        </div>
        <!-- User ID -->
        <div class="col-span-6 sm:col-span-12" v-if="helpers.userIsAdmin(currentUser)">
          <InputLabel for="user-id" value="User"/>
          <SelectInput id="user-id" v-model="eventForm.userId" :disabled="isModifyEventDisabled()"
                  class="mt-1 disabled:bg-gray-50">
            <option v-for="user in users" :value="user.employeeID" v-text="user.full_name"></option>
          </SelectInput>
        </div>
      </div>
    </template>
    <template #footer v-if="!isModifyEventDisabled()">
      <SecondaryButton class="mr-2" v-if="eventForm.id" @click.prevent="deleteEvent()"
                       :disabled="creatingNewEvent.saving">Delete
      </SecondaryButton>
      <PrimaryButton @click.prevent="saveEventForm()" :disabled="creatingNewEvent.saving">Save
      </PrimaryButton>
    </template>
  </DialogModal>
</template>
